import React, { useState, useEffect, useContext } from 'react';
import { Calendar, Clock, Video, MapPin, X } from 'lucide-react';
import api from '../api';
import { AuthContext } from '../context/AuthContext';

export default function InterviewScheduler({ applicationId, candidateName, jobTitle, onClose, onScheduled }) {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    scheduled_at: '',
    duration_minutes: 60,
    interview_type: 'video',
    meeting_link: '',
    location: '',
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/api/interviews', {
        application_id: applicationId,
        ...formData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (onScheduled) onScheduled();
      if (onClose) onClose();
    } catch (err) {
      console.error('Failed to schedule interview:', err);
      alert('Failed to schedule interview: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">Schedule Interview</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg mb-6">
            <div className="font-semibold">{candidateName}</div>
            <div className="text-sm text-gray-600">{jobTitle}</div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="inline mr-1" size={16} />
                  Date & Time *
                </label>
                <input
                  type="datetime-local"
                  value={formData.scheduled_at}
                  onChange={(e) => setFormData({...formData, scheduled_at: e.target.value})}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Clock className="inline mr-1" size={16} />
                  Duration (minutes)
                </label>
                <select
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={90}>1.5 hours</option>
                  <option value={120}>2 hours</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Interview Type
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="interview_type"
                    value="video"
                    checked={formData.interview_type === 'video'}
                    onChange={(e) => setFormData({...formData, interview_type: e.target.value})}
                    className="mr-2"
                  />
                  <Video size={16} className="mr-1" />
                  Video Call
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="interview_type"
                    value="phone"
                    checked={formData.interview_type === 'phone'}
                    onChange={(e) => setFormData({...formData, interview_type: e.target.value})}
                    className="mr-2"
                  />
                  Phone
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="interview_type"
                    value="in_person"
                    checked={formData.interview_type === 'in_person'}
                    onChange={(e) => setFormData({...formData, interview_type: e.target.value})}
                    className="mr-2"
                  />
                  In Person
                </label>
              </div>
            </div>

            {formData.interview_type === 'video' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Meeting Link
                </label>
                <input
                  type="url"
                  value={formData.meeting_link}
                  onChange={(e) => setFormData({...formData, meeting_link: e.target.value})}
                  placeholder="https://zoom.us/j/... or Google Meet link"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}

            {formData.interview_type === 'in_person' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <MapPin className="inline mr-1" size={16} />
                  Location
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  placeholder="Office address or meeting place"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes (optional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows="3"
                placeholder="Additional instructions or agenda for the interview..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50"
              >
                {loading ? 'Scheduling...' : 'Schedule Interview'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// Component to display scheduled interviews
export function InterviewsList() {
  const { token, user } = useContext(AuthContext);
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchInterviews = async () => {
    try {
      const res = await api.get('/api/interviews', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInterviews(res.data);
    } catch (err) {
      console.error('Failed to fetch interviews:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && token) {
      fetchInterviews();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'rescheduled': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <div className="text-center text-gray-500 py-4">Loading interviews...</div>;
  }

  if (interviews.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <Calendar className="mx-auto text-gray-300 mb-4" size={48} />
        <p className="text-gray-500">No interviews scheduled yet</p>
      </div>
    );
  }

  // Separate upcoming and past interviews
  const now = new Date();
  const upcoming = interviews.filter(i => new Date(i.scheduled_at) >= now && i.status !== 'cancelled');
  const past = interviews.filter(i => new Date(i.scheduled_at) < now || i.status === 'cancelled');

  return (
    <div className="space-y-6">
      {upcoming.length > 0 && (
        <div>
          <h3 className="text-lg font-bold mb-4">Upcoming Interviews</h3>
          <div className="space-y-3">
            {upcoming.map(interview => (
              <div key={interview.id} className="bg-white rounded-lg shadow p-4 hover:shadow-md transition">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold">{interview.job_title}</h4>
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(interview.status)}`}>
                        {interview.status}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div className="flex items-center gap-2">
                        <Calendar size={14} />
                        {new Date(interview.scheduled_at).toLocaleString()}
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock size={14} />
                        {interview.duration_minutes} minutes
                      </div>
                      {user?.user_type === 'employer' && (
                        <div>Candidate: {interview.candidate_name}</div>
                      )}
                      {user?.user_type === 'job_seeker' && (
                        <div>Employer: {interview.employer_name}</div>
                      )}
                      {interview.interview_type === 'video' && interview.meeting_link && (
                        <div className="flex items-center gap-2">
                          <Video size={14} />
                          <a href={interview.meeting_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            Join Meeting
                          </a>
                        </div>
                      )}
                      {interview.interview_type === 'in_person' && interview.location && (
                        <div className="flex items-center gap-2">
                          <MapPin size={14} />
                          {interview.location}
                        </div>
                      )}
                      {interview.notes && (
                        <div className="mt-2 text-gray-500 text-xs italic">{interview.notes}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {past.length > 0 && (
        <div>
          <h3 className="text-lg font-bold mb-4">Past Interviews</h3>
          <div className="space-y-3">
            {past.slice(0, 5).map(interview => (
              <div key={interview.id} className="bg-gray-50 rounded-lg p-4 opacity-75">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-sm">{interview.job_title}</h4>
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(interview.status)}`}>
                        {interview.status}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(interview.scheduled_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
